import React, { useEffect, useMemo, useRef, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckIcon from '@mui/icons-material/Check'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import ErrorIcon from '@mui/icons-material/Error'
import GridViewIcon from '@mui/icons-material/GridView'
import HelpIcon from '@mui/icons-material/Help'
import LightModeIcon from '@mui/icons-material/LightMode'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NotesIcon from '@mui/icons-material/Notes'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { get, limitToLast, query, ref } from 'firebase/database'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Link, useParams } from 'react-router'
import ably from '../ablyClient'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import { getLoginSession, updateBoardRelays } from '../services/authApi'
import { db } from '../services/firebase'
import './DashboardPage.css'

const ABLY_CHANNEL = process.env.REACT_APP_ABLY_CHANNEL || 'alarms'
const ABLY_EVENT = process.env.REACT_APP_ABLY_EVENT || 'alarm.triggered'
const LOG_FETCH_LIMIT = 24
const PIE_SIZE = 220
const PIE_CENTER = PIE_SIZE / 2
const PIE_RADIUS = 82

const fallbackSummaryCards = [
  {
    metric: 'Grid Import',
    value: 'N/A',
  },
  {
    metric: 'Grid Export',
    value: 'N/A',
  },
  {
    metric: 'Estimated Savings',
    value: 'N/A',
  },
]

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (!trimmedValue) {
      return null
    }

    const parsed = Number(trimmedValue)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function getLogPayload(log = {}) {
  if (!log || typeof log !== 'object') {
    return {}
  }

  return log?.data && typeof log.data === 'object' ? log.data : log
}

function getPayloadDeviceId(payload) {
  if (!payload || typeof payload !== 'object') {
    return ''
  }

  return payload?.config?.Device?.ID
    || payload?.data?.DeviceID
    || payload?.data?.deviceId
    || payload?.deviceId
    || payload?.id
    || ''
}

function getAblyBoardPayload(messageData, deviceId) {
  if (!messageData || typeof messageData !== 'object') {
    return null
  }

  if (messageData[deviceId]) {
    return messageData[deviceId]
  }

  if (messageData.data?.[deviceId]) {
    return messageData.data[deviceId]
  }

  return getPayloadDeviceId(messageData) === deviceId ? messageData : null
}

function getMlPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  return payload.ml || payload.data?.ml || payload.devicedata?.ml || null
}

function getPowerImport(log) {
  const payload = getLogPayload(log)

  return toNumber(payload.powerImport ?? payload.gridImport ?? payload.import ?? payload.importPower ?? log?.devicedata?.meter?.import?.W)
}

function getPowerExport(log) {
  const payload = getLogPayload(log)
  return toNumber(payload.powerExport ?? payload.gridExport ?? payload.export ?? payload.exportPower ?? log?.devicedata?.meter?.export?.W)
}

function getEstimatedSavings(log) {
  const payload = getLogPayload(log)

  return toNumber(
    payload.estimatedSavings
      ?? payload.estimatedSavingsPhp
      ?? payload.savings
      ?? payload.savingsPhp
      ?? log?.estimatedSavings
      ?? log?.estimatedSavingsPhp
      ?? log?.savings
      ?? log?.savingsPhp
  )
}

function formatKw(value) {
  const numericValue = toNumber(value)

  return numericValue === null ? 'N/A' : `${numericValue.toFixed(2)} kW`
}

function formatPhp(value) {
  const numericValue = toNumber(value)

  return numericValue === null ? 'N/A' : `PHP ${numericValue.toFixed(2)}`
}

function formatMetricValue(value, unit) {
  const numericValue = toNumber(value)

  return numericValue === null ? 'N/A' : `${numericValue.toFixed(2)} ${unit}`
}

function normalizeFirebaseLogs(rawLogs) {
  const logs = Array.isArray(rawLogs)
    ? rawLogs
        .map((value, index) => {
          if (!value || typeof value !== 'object') {
            return null
          }

          return {
            _key: String(index),
            timestamp: value.timestamp ?? value.ts ?? value.time ?? index,
            ...value,
          }
        })
        .filter(Boolean)
    : Object.entries(rawLogs || {}).map(([key, value]) => ({
        _key: key,
        timestamp: value?.timestamp ?? value?.ts ?? value?.time ?? key,
        ...(value && typeof value === 'object' ? value : {}),
      }))

  return logs
    .filter((log) => Number.isFinite(Number(log?.timestamp)))
    .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
}

function getRelay(log, relayKey) {
  const payload = getLogPayload(log)
  const control = payload?.config?.control || payload?.control || log?.config?.control || log?.control || {}
  const relayName = relayKey === 'rly1' ? 'relay1' : 'relay2'
  const relay = control?.relay?.[relayKey] ?? control[relayKey] ?? control[relayName] ?? {}
  const value = typeof relay === 'object'
    ? relay.state
    : relay ?? payload?.[relayKey] ?? payload?.[relayName]

  return {
    enabled: value === true || String(value) === '1',
    label: typeof relay === 'object' ? relay.label || `Relay ${relayKey === 'rly1' ? '1' : '2'}` : `Relay ${relayKey === 'rly1' ? '1' : '2'}`,
  }
}

function getStatusCode(log) {
  const payload = getLogPayload(log)
  const statusCode = payload?.ml?.status ?? payload?.status ?? payload?.statusCode ?? log?.ml?.status

  return statusCode === undefined || statusCode === null || statusCode === '' ? 'N/A' : String(statusCode)
}

function getPerformanceLabel(log) {
  const statusCode = getStatusCode(log)

  if (statusCode === '1') {
    return 'Grid'
  }

  if (statusCode === '2') {
    return 'Warning'
  }

  if (statusCode === '3') {
    return 'Normal'
  }

  return statusCode === 'N/A' ? 'Unavailable' : `Status ${statusCode}`
}

function getPerformanceTone(log) {
  const statusCode = getStatusCode(log)

  if (statusCode === '1') {
    return 'critical'
  }

  if (statusCode === '2') {
    return 'warning'
  }

  if (statusCode === '3') {
    return 'normal'
  }

  return 'unavailable'
}

function getPerformanceMessage(log) {
  const tone = getPerformanceTone(log)

  if (tone === 'normal') {
    return 'The system is operating within its expected parameters.'
  }

  if (tone === 'warning') {
    return 'Performance needs attention. Review the latest readings.'
  }

  if (tone === 'critical') {
    return 'The system is operating in grid mode. Review the latest power flow.'
  }

  return 'Waiting for a device health reading.'
}

function getFaultStatus(log) {
  const payload = getLogPayload(log)
  const faultStatus = payload?.ml?.fault ?? payload?.fault ?? payload?.faultStatus ?? log?.ml?.fault

  return faultStatus === undefined || faultStatus === null || faultStatus === '' ? 'N/A' : String(faultStatus)
}

function getFaultLabel(log) {
  const faultStatus = getFaultStatus(log)

  if (faultStatus === '0') {
    return 'No active faults'
  }

  if (faultStatus === 'N/A') {
    return 'Not available'
  }

  return `Fault ${faultStatus} detected`
}

function PerformanceStatusIcon({ tone }) {
  if (tone === 'normal') {
    return <CheckIcon aria-hidden="true" />
  }

  if (tone === 'warning') {
    return <WarningAmberIcon aria-hidden="true" />
  }

  if (tone === 'critical') {
    return <ErrorIcon aria-hidden="true" />
  }

  return <HelpIcon aria-hidden="true" />
}

function getDisplayName(user = {}) {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')

  return user.fullName || user.name || fullName || 'User'
}

function getAnalyticsPoints(logs) {
  return logs
    .slice()
    .reverse()
    .map((log) => ({
      timestamp: Number(log.timestamp),
      importValue: getPowerImport(log),
      exportValue: getPowerExport(log),
    }))
    .filter((point) => point.importValue !== null || point.exportValue !== null)
}

function getAverage(values) {
  const validValues = values.filter((value) => value !== null)

  if (!validValues.length) {
    return null
  }

  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length
}

function getPeak(values) {
  const validValues = values.filter((value) => value !== null)

  return validValues.length ? Math.max(...validValues) : null
}

function getTotal(values) {
  return values
    .filter((value) => value !== null)
    .reduce((sum, value) => sum + value, 0)
}

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

function getPieSlicePath(startAngle, endAngle) {
  const start = polarToCartesian(PIE_CENTER, PIE_CENTER, PIE_RADIUS, endAngle)
  const end = polarToCartesian(PIE_CENTER, PIE_CENTER, PIE_RADIUS, startAngle)
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

  return [
    `M ${PIE_CENTER} ${PIE_CENTER}`,
    `L ${start.x.toFixed(2)} ${start.y.toFixed(2)}`,
    `A ${PIE_RADIUS} ${PIE_RADIUS} 0 ${largeArcFlag} 0 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`,
    'Z',
  ].join(' ')
}

function EnergyPieChart({ importTotal, exportTotal }) {
  const total = importTotal + exportTotal

  if (total <= 0) {
    return (
      <div className="old-dashboard__analytics-empty">
        <span>No analytics data available</span>
      </div>
    )
  }

  const importPercent = (importTotal / total) * 100
  const exportPercent = 100 - importPercent
  const importEndAngle = importPercent === 100 ? 359.99 : (importPercent / 100) * 360
  const exportStartAngle = importEndAngle
  const exportEndAngle = 359.99
  const importLabel = polarToCartesian(PIE_CENTER, PIE_CENTER, 118, importEndAngle / 2)
  const exportLabel = polarToCartesian(PIE_CENTER, PIE_CENTER, 118, exportStartAngle + (exportEndAngle - exportStartAngle) / 2)

  const slices = []

  if (importTotal > 0) {
    slices.push({
      key: 'import',
      path: getPieSlicePath(0, importEndAngle),
      label: `Import ${importPercent.toFixed(0)}%`,
      labelX: importLabel.x,
      labelY: importLabel.y,
    })
  }

  if (exportTotal > 0) {
    slices.push({
      key: 'export',
      path: getPieSlicePath(exportStartAngle, exportEndAngle),
      label: `Export ${exportPercent.toFixed(0)}%`,
      labelX: exportLabel.x,
      labelY: exportLabel.y,
    })
  }

  return (
    <div className="old-dashboard__pie-panel">
      <svg className="old-dashboard__pie-chart" viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`} role="img" aria-label="Grid import and export distribution">
        <circle className="old-dashboard__pie-track" cx={PIE_CENTER} cy={PIE_CENTER} r={PIE_RADIUS} />
        {slices.map((slice) => (
          <React.Fragment key={slice.key}>
            <path className={`old-dashboard__pie-slice old-dashboard__pie-slice--${slice.key}`} d={slice.path} />
            <text className="old-dashboard__pie-label" x={slice.labelX} y={slice.labelY}>{slice.label}</text>
          </React.Fragment>
        ))}
        <circle className="old-dashboard__pie-hole" cx={PIE_CENTER} cy={PIE_CENTER} r="45" />
        <text className="old-dashboard__pie-total-label" x={PIE_CENTER} y={PIE_CENTER - 5}>Total</text>
        <text className="old-dashboard__pie-total" x={PIE_CENTER} y={PIE_CENTER + 16}>{formatMetricValue(total, 'kW')}</text>
      </svg>

      <div className="old-dashboard__pie-breakdown">
        <article>
          <span>Import Total</span>
          <strong>{formatMetricValue(importTotal, 'kW')}</strong>
        </article>
        <article>
          <span>Export Total</span>
          <strong>{formatMetricValue(exportTotal, 'kW')}</strong>
        </article>
      </div>
    </div>
  )
}

function EnergyBarChart({ averageImport, averageExport, peakImport, peakExport }) {
  const bars = [
    { label: 'Avg Import', value: averageImport, type: 'import' },
    { label: 'Avg Export', value: averageExport, type: 'export' },
    { label: 'Peak Import', value: peakImport, type: 'import' },
    { label: 'Peak Export', value: peakExport, type: 'export' },
  ]
  const maxValue = Math.max(...bars.map((bar) => bar.value || 0), 1)

  return (
    <div className="old-dashboard__bar-chart" role="img" aria-label="Average and peak grid comparison">
      {bars.map((bar) => (
        <div className="old-dashboard__bar-row" key={bar.label}>
          <span>{bar.label}</span>
          <div className="old-dashboard__bar-track">
            <i
              className={`old-dashboard__bar-fill old-dashboard__bar-fill--${bar.type}`}
              style={{ width: `${Math.max(((bar.value || 0) / maxValue) * 100, bar.value ? 8 : 0)}%` }}
            ></i>
          </div>
          <b>{formatMetricValue(bar.value, 'kW')}</b>
        </div>
      ))}
    </div>
  )
}

function SavingsPlaceholderChart({ savingsTotal }) {
  const hasSavings = savingsTotal !== null && savingsTotal > 0

  return (
    <div className="old-dashboard__placeholder-chart" role="img" aria-label="Estimated savings placeholder chart">
      <div className="old-dashboard__placeholder-ring">
        <span>{hasSavings ? formatPhp(savingsTotal) : 'N/A'}</span>
      </div>
      <div className="old-dashboard__placeholder-bars" aria-hidden="true">
        <i></i>
        <i></i>
        <i></i>
        <i></i>
      </div>
    </div>
  )
}

function DashboardPage({ isDarkMode = false, onSelectTheme }) {
  const { deviceId = '' } = useParams()
  const mapElementRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const displayName = getDisplayName(getLoginSession()?.user)
  const [recentLogs, setRecentLogs] = useState([])
  const [liveMl, setLiveMl] = useState(null)
  const [liveStatusUpdatedAt, setLiveStatusUpdatedAt] = useState(null)
  const [relayStates, setRelayStates] = useState({
    relay1: { enabled: false, label: 'Relay 1' },
    relay2: { enabled: false, label: 'Relay 2' },
  })
  const [isUpdatingRelays, setIsUpdatingRelays] = useState(false)
  const [relayError, setRelayError] = useState('')
  const latestLog = recentLogs[0] || null
  const performanceLog = liveMl
    ? {
        ml: {
          ...(getLogPayload(latestLog)?.ml || {}),
          ...(latestLog?.ml || {}),
          ...liveMl,
        },
      }
    : latestLog
  const performanceTone = getPerformanceTone(performanceLog)
  const logsPath = deviceId ? `/dashboard/${encodeURIComponent(deviceId)}/logs` : '/dashboard'
  const summaryCards = useMemo(() => {
    if (!latestLog) {
      return fallbackSummaryCards
    }

    return [
      {
        metric: 'Grid Import',
        value: formatKw(getPowerImport(latestLog)),
      },
      {
        metric: 'Grid Export',
        value: formatKw(getPowerExport(latestLog)),
      },
      {
        metric: 'Estimated Savings',
        value: formatPhp(getEstimatedSavings(latestLog)),
      },
    ]
  }, [latestLog])
  const analytics = useMemo(() => {
    const points = getAnalyticsPoints(recentLogs)
    const importValues = points.map((point) => point.importValue)
    const exportValues = points.map((point) => point.exportValue)
    const savingsValues = recentLogs.map((log) => getEstimatedSavings(log))
    const importTotal = getTotal(importValues)
    const exportTotal = getTotal(exportValues)

    return {
      points,
      averageImport: getAverage(importValues),
      averageExport: getAverage(exportValues),
      peakImport: getPeak(importValues),
      peakExport: getPeak(exportValues),
      importTotal,
      exportTotal,
      savingsTotal: savingsValues.some((value) => value !== null) ? getTotal(savingsValues) : null,
      sampleCount: points.length,
    }
  }, [recentLogs])

  useEffect(() => {
    if (!latestLog) {
      return
    }

    setRelayStates({
      relay1: getRelay(latestLog, 'rly1'),
      relay2: getRelay(latestLog, 'rly2'),
    })
  }, [latestLog])

  const handleRelayToggle = async (relayKey, enabled) => {
    const previousStates = relayStates
    const nextStates = {
      ...relayStates,
      [relayKey]: {
        ...relayStates[relayKey],
        enabled,
      },
    }

    setRelayStates(nextStates)
    setIsUpdatingRelays(true)
    setRelayError('')

    try {
      await updateBoardRelays(deviceId, nextStates)
    } catch (error) {
      setRelayStates(previousStates)
      setRelayError(error.message || 'Unable to update the relay controls.')
    } finally {
      setIsUpdatingRelays(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    if (!deviceId) {
      setRecentLogs([])

      return () => {
        isMounted = false
      }
    }

    const loadRecentLogs = async () => {
      try {
        const logsRef = query(ref(db, `EMS/Logs/${deviceId}/data`), limitToLast(LOG_FETCH_LIMIT))
        const snapshot = await get(logsRef)

        if (!isMounted) {
          return
        }

        setRecentLogs(snapshot.exists() ? normalizeFirebaseLogs(snapshot.val()) : [])
      } catch (error) {
        console.error(`Unable to fetch Firebase logs for ${deviceId}:`, error)

        if (isMounted) {
          setRecentLogs([])
        }
      }
    }

    loadRecentLogs()

    return () => {
      isMounted = false
    }
  }, [deviceId])

  useEffect(() => {
    setLiveMl(null)
    setLiveStatusUpdatedAt(null)

    if (!deviceId) {
      return undefined
    }

    const channel = ably.channels.get(ABLY_CHANNEL)
    const handler = ({ data = {} }) => {
      const updatePayload = getAblyBoardPayload(data, deviceId)
      const mlUpdate = getMlPayload(updatePayload)

      if (!mlUpdate) {
        return
      }

      setLiveMl((currentMl) => ({
        ...(currentMl || {}),
        ...mlUpdate,
      }))
      setLiveStatusUpdatedAt(Date.now())
    }

    channel.subscribe(ABLY_EVENT, handler)

    return () => {
      channel.unsubscribe(ABLY_EVENT, handler)
    }
  }, [deviceId])

  useEffect(() => {
    if (!mapElementRef.current || mapInstanceRef.current) {
      return undefined
    }

    const map = L.map(mapElementRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([12.8797, 121.7740], 5)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)

    mapInstanceRef.current = map
    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  return (
    <main className="old-dashboard">
      <section className="old-dashboard__shell" aria-label="CNERGI device dashboard">
        <aside className="old-dashboard__sidebar">
          <a className="old-dashboard__brand" href="/" aria-label="CNERGI home">
            <img src={cnergiLogo} alt="CNERGI" />
          </a>

          <p className="old-dashboard__menu-label">menu</p>

          <nav className="old-dashboard__nav" aria-label="Dashboard navigation">
            <button className="old-dashboard__nav-button old-dashboard__nav-button--active" type="button" aria-label="Dashboard">
              <GridViewIcon aria-hidden="true" />
            </button>
            <Link
              className="old-dashboard__nav-button"
              to={logsPath}
              aria-label="Logs"
              title="Logs"
            >
              <NotesIcon aria-hidden="true" />
            </Link>
            <a className="old-dashboard__nav-button" href="/maindash" aria-label="Back to main dashboard">
              <ArrowBackIcon aria-hidden="true" />
            </a>
          </nav>

          <a className="old-dashboard__support" href="#support" aria-label="Support">
            <SupportAgentIcon aria-hidden="true" />
          </a>
        </aside>

        <section className="old-dashboard__main" aria-labelledby="old-dashboard-title">
          <header className="old-dashboard__header">
            <h1 id="old-dashboard-title">{deviceId ? `Dashboard ${deviceId}` : 'Dashboard'}</h1>

            <div className="old-dashboard__header-actions">
              <button
                className="old-dashboard__theme-toggle"
                type="button"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                onClick={() => onSelectTheme?.(isDarkMode ? 'light' : 'dark')}
              >
                {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
              </button>

              <div className="old-dashboard__user">
                <AccountCircleIcon aria-hidden="true" />
                <strong>{displayName}</strong>
              </div>

              <button className="old-dashboard__more" type="button" aria-label="More options">
                <MoreVertIcon aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="old-dashboard__summary">
            {summaryCards.map((card) => (
              <article className="old-dashboard__summary-card" key={card.metric}>
                <p>{card.metric}</p>
                <b>{card.value}</b>
              </article>
            ))}

            <article className="old-dashboard__summary-card old-dashboard__controls-card">
              <span>Controls</span>
              <p>{deviceId || 'No device selected'}</p>
              <div className="old-dashboard__relay-row">
                <span>{relayStates.relay1.label}</span>
                <label className="old-dashboard__switch">
                  <input
                    type="checkbox"
                    checked={relayStates.relay1.enabled}
                    onChange={(event) => handleRelayToggle('relay1', event.target.checked)}
                    disabled={!deviceId || isUpdatingRelays}
                  />
                  <span></span>
                </label>
              </div>
              <div className="old-dashboard__relay-row">
                <span>{relayStates.relay2.label}</span>
                <label className="old-dashboard__switch">
                  <input
                    type="checkbox"
                    checked={relayStates.relay2.enabled}
                    onChange={(event) => handleRelayToggle('relay2', event.target.checked)}
                    disabled={!deviceId || isUpdatingRelays}
                  />
                  <span></span>
                </label>
              </div>
              {relayError ? <p className="old-dashboard__relay-error" role="alert">{relayError}</p> : null}
            </article>
          </div>

          <section className="old-dashboard__analytics" aria-labelledby="analytics-title">
            <div className="old-dashboard__analytics-header">
              <div>
                <h2 id="analytics-title">Analytics</h2>
                <p>Recent grid activity from the latest {analytics.sampleCount || 0} readings</p>
              </div>
              <div className="old-dashboard__analytics-legend" aria-label="Chart legend">
                <span><i className="old-dashboard__legend-mark old-dashboard__legend-mark--import"></i>Import</span>
                <span><i className="old-dashboard__legend-mark old-dashboard__legend-mark--export"></i>Export</span>
              </div>
            </div>

            <div className="old-dashboard__chart-grid">
              <article className="old-dashboard__chart-card old-dashboard__chart-card--wide">
                <h3>Energy Mix</h3>
                <EnergyPieChart importTotal={analytics.importTotal} exportTotal={analytics.exportTotal} />
              </article>

              <article className="old-dashboard__chart-card">
                <h3>Load Comparison</h3>
                <EnergyBarChart
                  averageImport={analytics.averageImport}
                  averageExport={analytics.averageExport}
                  peakImport={analytics.peakImport}
                  peakExport={analytics.peakExport}
                />
              </article>

              <article className="old-dashboard__chart-card">
                <h3>Savings</h3>
                <SavingsPlaceholderChart savingsTotal={analytics.savingsTotal} />
              </article>
            </div>

            <div className="old-dashboard__analytics-stats">
              <article>
                <span>Avg Import</span>
                <strong>{formatMetricValue(analytics.averageImport, 'kW')}</strong>
              </article>
              <article>
                <span>Avg Export</span>
                <strong>{formatMetricValue(analytics.averageExport, 'kW')}</strong>
              </article>
              <article>
                <span>Peak Import</span>
                <strong>{formatMetricValue(analytics.peakImport, 'kW')}</strong>
              </article>
              <article>
                <span>Peak Export</span>
                <strong>{formatMetricValue(analytics.peakExport, 'kW')}</strong>
              </article>
            </div>
          </section>

        </section>

        <aside className="old-dashboard__aside">
          <section className="old-dashboard__status-card" aria-labelledby="performance-title">
            <h2 id="performance-title">Performance Status</h2>
            <div className={`old-dashboard__critical old-dashboard__critical--${performanceTone}`}>
              <div className="old-dashboard__status-heading">
                <span className="old-dashboard__status-icon">
                  <PerformanceStatusIcon tone={performanceTone} />
                </span>
                <span className="old-dashboard__status-kicker">System health</span>
              </div>

              <div className="old-dashboard__status-copy">
                <strong>{getPerformanceLabel(performanceLog)}</strong>
                <p>{getPerformanceMessage(performanceLog)}</p>
              </div>

              <div className="old-dashboard__status-details">
                <div>
                  <span>Faults</span>
                  <strong>{getFaultLabel(performanceLog)}</strong>
                </div>
                <div>
                  <span>Status code</span>
                  <strong>{getStatusCode(performanceLog)}</strong>
                </div>
              </div>

              <div className="old-dashboard__status-live">
                <i aria-hidden="true"></i>
                <span>{liveStatusUpdatedAt ? 'Live update received just now' : 'Live monitoring enabled'}</span>
              </div>
            </div>
          </section>

          <section className="old-dashboard__location-card" aria-labelledby="location-title">
            <h2 id="location-title">Location</h2>
            <div className="old-dashboard__mini-map" ref={mapElementRef} aria-label="OpenStreetMap device location"></div>
          </section>
        </aside>
      </section>
    </main>
  )
}

export default DashboardPage
